import React, { useEffect, useState } from "react";
import { Spin, Tooltip, Upload, message } from "antd";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import PropTypes from "prop-types";

import { BASE_CRM_URL } from "../../../components/config/config";
import Table from "../../../../core/pagination/datatable";
import CommonFooter from "../../../../core/common/footer/commonFooter";

const ProjectFileUpload = ({ CustomerData }) => {
  console.log(CustomerData, "customerData..................");
  const customerId = CustomerData?.id;
  const userDetails = JSON.parse(sessionStorage.getItem("userDetails"));
  const userId = userDetails?.staffid;

  const { Dragger } = Upload;

  const [filesData, setFilesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  console.log(selectedFiles);

  const renderTooltip = (text) => {
    if (!text) return "N/A";

    return text.length > 30 ? (
      <Tooltip title={text}>
        <span>{text.substring(0, 30)}...</span>
      </Tooltip>
    ) : (
      text
    );
  };

  // ============================
  // GET FILES
  // ============================
  const getFiles = async () => {
    if (!customerId) {
      console.log("customerId is missing");
      return;
    }

    try {
      setLoading(true);

      // Replace this endpoint with your actual GET API
      const response = await fetch(
        `${BASE_CRM_URL}Files/GetFiles?rel_id=${customerId}&rel_type=project`,
      );

      const data = await response.json();

      console.log("GET FILES RESPONSE:", data);

      if (data?.Status) {
        setFilesData(data?.ResultData || []);
      } else {
        setFilesData([]);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
      setFilesData([]);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load files.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================
  // GET FILES ON LOAD
  // ============================
  useEffect(() => {
    if (customerId) {
      getFiles();
    }
  }, [customerId]);

  // ============================
  // FILE SELECT
  // ============================
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    // 10 MB validation
    const invalidFiles = files.filter((file) => file.size > 10 * 1024 * 1024);

    if (invalidFiles.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "File Too Large",
        text: "Each file must be less than 10 MB.",
      });

      e.target.value = "";
      return;
    }

    setSelectedFiles(files);

    // Immediately upload
    uploadFiles(files);

    // Reset input so same file can be selected again
    e.target.value = "";
  };

  // ============================
  // ADD FILE API
  // ============================
  const uploadFiles = async (files) => {
    if (!customerId) {
      Swal.fire({
        icon: "warning",
        title: "project ID Missing",
        text: "project ID is required to upload a file.",
      });
      return;
    }

    if (!userId) {
      Swal.fire({
        icon: "warning",
        title: "Staff ID Missing",
        text: "Staff ID is required to upload a file.",
      });
      return;
    }

    try {
      setUploading(true);

      for (const file of files) {
        const formData = new FormData();

        formData.append("file", file);
        formData.append("rel_id", customerId);
        formData.append("rel_type", "project");
        formData.append("staffid", userId);

        console.log("UPLOAD DATA:", {
          file: file.name,
          rel_id: customerId,
          rel_type: "project",
          staffid: userId,
        });

        const response = await fetch(`${BASE_CRM_URL}customers/AddFile`, {
          method: "POST",
          body: formData,
        });

        const data = await response.json();

        console.log("UPLOAD RESPONSE:", data);

        if (!response.ok || !data?.Status) {
          throw new Error(data?.Message || `Failed to upload ${file.name}`);
        }
      }

      Swal.fire({
        icon: "success",
        title: "Uploaded!",
        text:
          files.length === 1
            ? "File uploaded successfully."
            : "Files uploaded successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      setSelectedFiles([]);

      // Refresh table
      getFiles();
    } catch (error) {
      console.error("File upload error:", error);

      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: error.message || "Something went wrong while uploading.",
      });
    } finally {
      setUploading(false);
    }
  };

  // ============================
  // TABLE COLUMNS
  // ============================
  const columns = [
    {
      title: "File",
      dataIndex: "file_name",

      sorter: (a, b) => (a.file_name || "").localeCompare(b.file_name || ""),

      render: (file_name, record) => {
        if (!file_name) return "N/A";

        const ext = file_name.split(".").pop().toLowerCase();

        let icon = "fa-file";
        let color = "text-secondary";

        if (ext === "pdf") {
          icon = "fa-file-pdf";
          color = "text-danger";
        } else if (["jpg", "jpeg", "png"].includes(ext)) {
          icon = "fa-file-image";
          color = "text-success";
        } else if (["doc", "docx"].includes(ext)) {
          icon = "fa-file-word";
          color = "text-primary";
        } else if (["xls", "xlsx"].includes(ext)) {
          icon = "fa-file-excel";
          color = "text-success";
        }

        const fileUrl = record?.external_link?.trim() || null;

        return (
          <div className="d-flex align-items-center">
            <i className={`fa-solid ${icon} ${color} me-2 fs-4`} />

            {fileUrl ? (
              <a
                href={fileUrl}
                download={file_name}
                className="fw-medium text-decoration-none"
                title="Download file"
              >
                {renderTooltip(file_name)}
              </a>
            ) : (
              <span className="fw-medium">{renderTooltip(file_name)}</span>
            )}
          </div>
        );
      },
    },

    // {
    //   title: "Show to Customer",
    //   dataIndex: "showToCustomer",
    //   align: "center",

    //   sorter: (a, b) =>
    //     Number(a.showToCustomer) -
    //     Number(b.showToCustomer),

    //   render: (value) => (
    //     <Switch
    //       checked={Boolean(value)}
    //       size="small"
    //     />
    //   ),
    // },

    {
      title: "Date Uploaded",
      dataIndex: "dateadded",

      sorter: (a, b) => dayjs(a.dateadded).unix() - dayjs(b.dateadded).unix(),

      render: (date) => {
        if (!date) return "N/A";

        return (
          <div>
            <i className="fa-regular fa-calendar text-primary me-2"></i>
            {dayjs(date).format("DD/MM/YYYY hh:mm A")}
          </div>
        );
      },
    },

    {
      title: "Uploaded By",
      dataIndex: "uploaded_by",
      sorter: (a, b) =>
        `${a.firstname} ${a.lastname}`.localeCompare(
          `${b.firstname} ${b.lastname}`,
        ),

      render: (_, record) => <span>{record.uploaded_by || "-"}</span>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      align: "center",
      render: (_, record) => {
        const fileUrl = record?.external_link?.trim() || null;
        return (
          <div className="action-table-data">
            {" "}
            <div className="edit-delete-action">
              {" "}
              {fileUrl ? (
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="me-2 p-2 shadow-sm"
                  title="Download / View file"
                >
                  {" "}
                  <i className="ti ti-download text-primary"></i>{" "}
                </a>
              ) : (
                <span
                  className="me-2 p-2 shadow-sm text-muted"
                  title="File not available"
                >
                  {" "}
                  <i className="ti ti-download"></i>{" "}
                </span>
              )}{" "}
            </div>{" "}
          </div>
        );
      },
    },
  ];

  const uploadProps = {
    name: "file",
    multiple: true,
    accept: ".jpg,.jpeg,.png,.pdf,.docx,.xlsx",
    showUploadList: false,
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;

      if (!isLt10M) {
        message.error(`${file.name} is larger than 10 MB`);
        return Upload.LIST_IGNORE;
      }

      handleFileChange({
        target: {
          files: [file],
        },
      });

      return false;
    },
    disabled: uploading,
  };

  return (
    <div>
      {/* ============================
          UPLOAD CARD
      ============================ */}
      <div className="card border-0 shadow-sm">
        {/* <div className="card-body">
          <div className="mb-2">
            <p className="text-muted mb-0">
              Files from projects and tasks linked to the customer are not shown
              in this table.
            </p>
          </div>

          <div
            className="border border-2 border-primary-subtle rounded-3 bg-light bg-opacity-10 p-2 text-center"
            style={{ borderStyle: "dashed" }}
          >
            <div className="mb-3">
              <i className="fa-solid fa-cloud-arrow-up text-primary fa-3x"></i>
            </div>

            <h5 className="fw-semibold">Drag & Drop files here</h5>

            <p className="text-muted mb-3">
              or click below to browse your computer
            </p>

            <label
              className={`btn btn-primary px-4 ${uploading ? "disabled" : ""}`}
            >
              {uploading ? (
                <>
                  <Spin size="small" className="me-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-folder-open me-2"></i>
                  Choose Files
                </>
              )}

              <input
                type="file"
                multiple
                hidden
                disabled={uploading}
                onChange={handleFileChange}
              />
            </label>

            <div className="mt-3 small text-muted">
              Supported: JPG, PNG, PDF, DOCX, XLSX (Max 10 MB)
            </div>
          </div>
        </div> */}
        <div className="card-body">
          <Dragger {...uploadProps}>
            <div className="py-3">
              <p className="ant-upload-drag-icon">
                {uploading ? (
                  <Spin size="large" />
                ) : (
                  <i className="fa-solid fa-cloud-arrow-up text-primary fa-3x"></i>
                )}
              </p>

              <p className="ant-upload-text fw-semibold">
                {uploading ? "Uploading..." : "Drag & Drop files here"}
              </p>

              <p className="ant-upload-hint">
                or click below to browse your computer
              </p>

              <button
                type="button"
                className="btn btn-primary px-4"
                disabled={uploading}
                onClick={(e) => e.stopPropagation()}
              >
                <i className="fa-solid fa-folder-open me-2"></i>
                Choose Files
              </button>

              <div className="mt-3 small text-muted">
                Supported: JPG, PNG, PDF, DOCX, XLSX (Max 10 MB)
              </div>
            </div>
          </Dragger>
        </div>
      </div>

      {/* ============================
          FILE LIST
      ============================ */}
      <div className="card table-list-card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <div className="search-set p-3"></div>

          <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
            <div className="page-btn"></div>
          </div>
        </div>

        <div className="card-body">
          <div className="table-responsive">
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "250px",
                }}
              >
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={filesData}
                rowKey={(record) => record.id || record.key}
              />
            )}
          </div>
        </div>
      </div>

      <CommonFooter />
    </div>
  );
};

ProjectFileUpload.propTypes = {
  CustomerData: PropTypes.object,
};

export default ProjectFileUpload;
